import type { MetaFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";
import dayjs from 'dayjs';
import { ArrowRight, CircleDotDashed, CircleUser, MapPin, SearchIcon, X } from 'lucide-react';
import { useEffect, useReducer, useState } from "react";
import { Button, Dialog, DialogTrigger } from "react-aria-components";
import { Modal } from "~/components/Modal";
import { SearchField } from "~/components/Searchfield";
import { Select, SelectItem } from "~/components/Select";
import { ToggleButton } from "~/components/ToggleButton";
import { handleize, transformName } from "~/library/utilities";
import { useRootLoaderData } from "~/root";

export const meta: MetaFunction = () => {
  return [
    { title: "Welten" },
  ];
};

const reducerFn = (state: any, { type, payload }: any) => {
  switch (type) {
    case "ADD_FILTER":
    return {
      ...state,
      filters: {
        ...state.filters,
        [payload.key]: payload.value
      }
    }
    case "CLEAR_FILTERS":
      return {
        ...state,
        filters: {}
      }

    case "SET_PRODUCTS": {
      return {
        ...state,
        jobs: payload
      }
    }
  }
};

export default function Vacatures() {
  const {jobs, filters} = useRootLoaderData()

  const [searchQuery, setSearchQuery] = useState('')
  const [state, dispatch] = useReducer(reducerFn, {
    filters: {},
    jobs: []
  });

  const [sort, setSort] = useState('newOld')

  useEffect(() => {
    dispatch({ type: "SET_PRODUCTS", payload: jobs })
  }, [])

  const getJobs = (state: any, sort: any) => {
    let filteredJobs = state.jobs || []

    if (state.filters.locatie) {
      filteredJobs = filteredJobs.filter((job: any) => job?.job?.byner__Job_location__c === state.filters.locatie)
    }

    if (state.filters.expertisegebied) {
      filteredJobs = filteredJobs.filter((job: any) => job?.job?.Vakgebied__c === state.filters.expertisegebied)
    }

    if (state.filters.opdrachtgever) {
      filteredJobs = filteredJobs.filter((job: any) => job?.account?.Name === state.filters.opdrachtgever)
    }

    if (sort === 'oldNew') {
      filteredJobs = filteredJobs.slice().reverse();
    }
    
    return filteredJobs
  }

  const getSearchResults = (query: string) => {
    let filteredJobs: any = []
    let filteredByNaam = []
    let filteredByLocatie = []
    let filteredByniveau = []
    let filteredByOpdrachtgever = []

    if (searchQuery.length > 0) {
      filteredByNaam = jobs.filter((job: any) => job.job.Name?.toLowerCase().includes(query?.toLowerCase()))
      filteredByLocatie = jobs.filter((job: any) => job?.job.byner__Job_location__c?.toLowerCase().includes(query?.toLowerCase()))
      filteredByniveau = jobs.filter((job: any) => job?.job.Niveau__c?.toLowerCase().includes(query?.toLowerCase()))
      filteredByOpdrachtgever = jobs.filter((job: any) => job.account.Name?.toLowerCase().includes(query?.toLowerCase()))

      filteredJobs = [...filteredByNaam, ...filteredByLocatie, ...filteredByniveau, ...filteredByOpdrachtgever]
    }

    return filteredJobs
  }

  return (
    <div className="u-p-12 sm:u-p-20">
      <div className="u-max-w-screen-xl u-mx-auto">
        <h1 className="u-text-blue u-font-black u-uppercase u-italic u-leading-[4rem] u-text-[3.4rem] sm:u-text-[5.2rem] sm:u-leading-[5.2rem] u-font-proxima-nova">
          Vacatures <span className="u-text-pink">opdrachtgevers</span>
        </h1>

        <div className="u-flex u-gap-x-8 u-gap-y-8 u-flex-col sm:u-flex-row u-items-center u-mt-16 sm:u-mt-20">
          <div className="u-relative u-flex u-flex-col sm:u-flex-row u-w-full sm:u-w-auto u-gap-y-8 u-gap-x-8">
            <div>
              <Select
                label="Locatie"
                placeholder="Locatie"
                icon={<MapPin />}
                selectedKey={state?.filters['locatie'] || ''}
                onSelectionChange={(value: any) => {
                  dispatch({ 
                    type: "ADD_FILTER",
                      payload: { 
                      key: "locatie", 
                      value: value 
                    }
                  });
                }}
              >
                {filters?.locaties?.map((item: any) => {
                  return (
                    <SelectItem id={item} key={item}>
                      {item}
                    </SelectItem>
                  )
                })}
              </Select>
            </div>

            <div>
              <Select
                label="Expertisegebied"
                placeholder="Expertisegebied"
                icon={<CircleDotDashed />}
                selectedKey={state?.filters['expertisegebied'] || ''}
                onSelectionChange={(value: any) => {
                  dispatch({ 
                    type: "ADD_FILTER",
                      payload: { 
                      key: "expertisegebied", 
                      value: value 
                    }
                  });
                }}
              >
                {filters?.expertisegebieden?.map((item: any) => {
                  return (
                    <SelectItem id={item} key={item}>
                      {item}
                    </SelectItem>
                  )
                })}
              </Select>
            </div>

            <div>
              <Select 
                label="Opdrachtgever"
                placeholder="Opdrachtgever"
                icon={<CircleUser />}
                selectedKey={state?.filters['opdrachtgever'] || ''}
                onSelectionChange={(value: any) => {
                  dispatch({ 
                    type: "ADD_FILTER",
                      payload: { 
                      key: "opdrachtgever", 
                      value: value 
                    }
                  });
                }}
              >
                {filters?.opdrachtgevers.map((item: any) => {
                  return (
                    <SelectItem id={item} key={item}>
                      {transformName(item)}
                    </SelectItem>
                  )
                })}
              </Select>
            </div>
          </div>

          <div className="u-w-full sm:u-w-auto">
            <DialogTrigger>
              <Button className="focus:u-outline-none u-bg-pink u-w-full sm:u-w-auto u-justify-center u-text-white u-items-center u-font-bold u-p-4 u-h-20 u-flex u-gap-x-4 u-px-8 u-py-2">
                <SearchIcon className="u-w-8 u-h-8" />
                <span className="u-italic u-font-extrabold u-text-[1.4rem]">Zoek een vacature</span>
              </Button>

              <Modal isDismissable>
                <Dialog>
                  {({ close }) => (
                    <form>
                      <div className="u-relative">
                        <div className="u-group u-relative">
                          <div className="u-sticky u-top-0 u-bg-white u-z-10 u-px-7 u-pb-12 u-pt-14">

                            <Button onPress={close} className="u-absolute u-right-2 u-top-2 u-z-20">
                              <X className="u-w-8 u-h-8" />
                            </Button>

                            <SearchField label="Search" onChange={setSearchQuery} />

                            {getSearchResults(searchQuery).length > 0 &&
                              <div className="u-mt-8">
                                {getSearchResults(searchQuery).length} {getSearchResults(searchQuery).length > 1 ? <>resultaten</> : <>resultaat</> } gevonden
                              </div>
                            }
                          </div>

                          <div className="u-relative u-overflow-hidden">
                            {getSearchResults(searchQuery).map((job: any, index: number) => {
                              return (
                                <Link to={`/vacature/${handleize(job.job.Name)}`} key={index} className="u-p-12 u-block sm:hover:u-bg-blue-10 odd:u-bg-blue/5 sm:odd:u-bg-blue/0">
                                  <h4 className="u-uppercase u-text-pink u-font-proxima-nova u-font-extrabold u-text-[2.2rem] u-leading-[2.6rem] u-italic">
                                    {job.job.Name}
                                  </h4>

                                  <ul className="u-mt-4">
                                    <li>{job.job.byner__Job_location__c}</li>
                                    <li>{job.job.Niveau__c}</li>
                                    <li>{job.job.Vakgebied__c}</li>
                                  </ul>
                                </Link>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    </form>
                  )}
                </Dialog>
              </Modal>
            </DialogTrigger>
          </div>

          {Object.keys(state?.filters).length > 0 && (
            <div>
              <button 
                onClick={() => dispatch({ type: "CLEAR_FILTERS" })}
                className="u-text-pink u-block u-font-bold"
              >
                  Wis filters
                </button>
            </div>
          )}

          <div className="md:u-ml-auto">
            <div className="u-flex">
              <ToggleButton isSelected={sort === 'newOld'} onChange={() => setSort("newOld")}>Nieuw - Oud</ToggleButton>
              <span className="u-text-pink u-px-4 u-font-black">/</span>
              <ToggleButton isSelected={sort === 'oldNew'} onChange={() => setSort("oldNew")}>Oud - Nieuw</ToggleButton>
            </div>
          </div>
        </div>

        <div className="u-grid u-grid-cols-1 sm:u-grid-cols-2 lg:u-grid-cols-3 u-gap-16 u-mt-16 sm:u-mt-20 u-z-0">
          {getJobs(state, sort).map((job: any) => {
            return (
              <Tile key={job?.Name} {...job} />
            )
          })}
        </div>
      </div>
    </div>
  );
}

const Tile = ({...props}) => {
  const { job, account } = props

  return (
    <article className="u-bg-blue">
      <Link to={`/vacature/${handleize(job.Name)}`} className="u-block u-h-full u-relative u-min-h-[25rem] u-group">
        {account?.Name && (
          <div className="u-bg-pink u-absolute u-inline-block u-py-2 u-pl-4 u-pr-6 u-overflow-hidden">
            <span className="u-text-white u-font-proxima-nova u-font-extrabold u-italic">{transformName(account?.Name)}</span>
            <span className="u-bg-blue u-w-4 u-h-[110%] u-top-1/2 u--translate-y-1/2 u-rotate-12 u-translate-x-2 u-absolute u-right-0"></span>
          </div>
        )}

        <div className="u-pl-12 u-pr-20 u-pb-12 u-pt-24 u-flex u-flex-col u-h-full">
          {job?.Name && (
            <div className="u-flex-1">
              <h4 className="u-text-white u-break-words u-uppercase u-font-proxima-nova u-font-extrabold u-text-[2.2rem] u-leading-[2.6rem] u-italic">
                {job?.Name} 
              </h4>
            </div>
          )}

          <ul className="u-mt-8 u-text-white u-flex u-flex-col">
            <li>{job?.byner__Job_location__c}</li>
            <li>{job?.Vakgebied__c}</li>
            <li>{job?.Niveau__c}</li>
            <li>{dayjs(job?.CreatedDate).format('DD-MM-YYYY')}</li>
          </ul>
        </div>

        <Button className="u-absolute u-right-0 u-bottom-0 u-z-20 u-bg-pink u-w-16 u-h-16 u-flex u-items-center u-justify-center">
          <ArrowRight className="u-w-8 u-h-8 u-text-white" />
        </Button>
      </Link>
    </article>
  )
}
